import { useRef, type KeyboardEvent } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { HIDDEN_SCROLLBAR_CLASS } from '@/styles/scrollbar';
import {
  SEGMENT_ACTIVE_CLASS,
  SEGMENT_BASE_CLASS,
  SEGMENT_INACTIVE_CLASS,
  SEGMENTED_TRACK_CLASS,
} from '@/styles/segmentedControl';
import { formatNumber } from '@/utils/number';

export interface TableTab<TTabId extends string> {
  id: TTabId;
  label: string;
  count?: number | undefined;
  isCountLoading?: boolean | undefined;
  countTitle?: string | undefined;
}

interface TableTabsProps<TTabId extends string> {
  tabs: readonly TableTab<TTabId>[];
  activeTabId: TTabId;
  onTabChange: (tabId: TTabId) => void;
  ariaLabel: string;
}

const STEP_BY_KEY: Partial<Record<string, number>> = { ArrowRight: 1, ArrowLeft: -1 };
const FIRST_TAB_KEY = 'Home';
const LAST_TAB_KEY = 'End';

interface TabCountChipProps {
  tab: TableTab<string>;
  isActive: boolean;
}

function TabCountChip({ tab, isActive }: TabCountChipProps) {
  if (tab.isCountLoading) {
    return (
      <Skeleton
        aria-hidden
        className={cn('h-5 w-6 rounded-full', isActive ? 'bg-brand-link/25' : 'bg-white/20')}
      />
    );
  }
  if (tab.count === undefined) return null;
  return (
    <span
      className={cn(
        'min-w-6 rounded-full px-1.5 py-0.5 text-center text-xs font-medium tabular-nums',
        isActive ? 'bg-brand-link text-white' : 'bg-white/15 text-white/90',
      )}
      title={tab.countTitle}
    >
      {formatNumber(tab.count)}
    </span>
  );
}

function getTargetTabIndex(key: string, currentIndex: number, tabCount: number) {
  if (key === FIRST_TAB_KEY) return 0;
  if (key === LAST_TAB_KEY) return tabCount - 1;
  const step = STEP_BY_KEY[key];
  if (step === undefined) return undefined;
  return (currentIndex + step + tabCount) % tabCount;
}

export function TableTabs<TTabId extends string>({
  tabs,
  activeTabId,
  onTabChange,
  ariaLabel,
}: TableTabsProps<TTabId>) {
  const tabButtons = useRef<(HTMLButtonElement | null)[]>([]);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const activeIndex = tabs.findIndex((tab) => tab.id === activeTabId);
    const targetIndex = getTargetTabIndex(event.key, activeIndex, tabs.length);
    if (targetIndex === undefined) return;
    const targetTab = tabs[targetIndex];
    if (targetTab === undefined) return;

    event.preventDefault();
    onTabChange(targetTab.id);
    tabButtons.current[targetIndex]?.focus();
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      className={cn(
        'flex w-fit max-w-full shrink-0 gap-1 overflow-x-auto',
        SEGMENTED_TRACK_CLASS,
        HIDDEN_SCROLLBAR_CLASS,
      )}
    >
      {tabs.map((tab, index) => {
        const isActive = tab.id === activeTabId;

        return (
          <button
            key={tab.id}
            ref={(button) => {
              tabButtons.current[index] = button;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onTabChange(tab.id)}
            className={cn(
              'inline-flex h-10 shrink-0 items-center gap-2 px-3.5 text-sm whitespace-nowrap lg:h-7',
              SEGMENT_BASE_CLASS,
              isActive ? SEGMENT_ACTIVE_CLASS : SEGMENT_INACTIVE_CLASS,
            )}
          >
            {tab.label}
            <TabCountChip tab={tab} isActive={isActive} />
          </button>
        );
      })}
    </div>
  );
}
